<template>
  <article
    class="faq-page"
    :class="{ 'faq-page--dark': $vuetify.theme.dark }"
    lang="en"
    aria-labelledby="faq-title"
  >
    <div class="faq-layout">
      <aside class="faq-index">
        <details :open="$vuetify.breakpoint.mdAndUp">
          <summary>On this page</summary>
          <nav aria-label="FAQ topics">
            <a
              v-for="section in content.sections"
              :key="section.id"
              :href="`#${section.id}`"
            >{{ section.title }}</a>
          </nav>
        </details>
      </aside>
      <div class="faq-body">
        <header class="faq-intro">
          <p class="faq-eyebrow">
            {{ content.title }}
          </p>
          <h1 id="faq-title">
            {{ content.headline }}
          </h1>
          <p>{{ content.intro }}</p>
        </header>
        <section
          v-for="section in content.sections"
          :key="section.id"
          class="faq-section"
          :aria-labelledby="section.id"
        >
          <h2 :id="section.id">
            {{ section.title }}
          </h2>
          <details
            v-for="item in section.questions"
            :id="item.id"
            :key="item.id"
            class="faq-question"
            :open="item.id === 'faq-1'"
          >
            <summary>{{ item.question }}</summary>
            <div class="faq-answer">
              <p
                v-for="(paragraph, index) in item.answer"
                :key="index"
              >
                {{ paragraph }}
              </p>
            </div>
          </details>
        </section>
        <div class="faq-more">
          <p>Discover the idea behind Self Biography.</p>
          <router-link to="/pages/self-biography">
            About Self Biography
          </router-link>
        </div>
      </div>
    </div>
  </article>
</template>

<script>
import content from '@/content/selfBiographyFaq.json'

export default {
  name: 'SelfBiographyFaq',
  data: () => ({ content, previousTitle: '' }),
  mounted() {
    this.previousTitle = document.title
    document.title = 'Frequently Asked Questions | Self Biography'
    this.$nextTick(() => {
      const id = this.$route.hash.slice(1)
      const target = [...this.content.sections.map(section => section.id), ...this.content.sections.flatMap(section => section.questions.map(item => item.id))].includes(id) && document.getElementById(id)
      if (target) {
        if (target.tagName === 'DETAILS') target.open = true
        target.scrollIntoView()
      } else {
        window.scrollTo(0, 0)
      }
    })
  },
  beforeDestroy() {
    document.title = this.previousTitle
  },
}
</script>

<style lang="scss" scoped>
.faq-page {
  --faq-text: #5e5669;
  --faq-heading: #393345;
  --faq-accent: #713ac7;
  --faq-surface: #fff;
  --faq-tint: #f3eefb;
  --faq-divider: rgba(94, 86, 105, 0.16);
  max-width: 1160px;
  margin: 32px auto 48px;
  color: var(--faq-text);
  font-size: 16px;
  line-height: 1.8;
  text-align: left;
  &--dark {
    --faq-text: #d0cadf;
    --faq-heading: #eee9fa;
    --faq-accent: #c6a5ff;
    --faq-surface: #312d4b;
    --faq-tint: #39304f;
    --faq-divider: rgba(231, 227, 252, 0.16);
  }
  h1, h2 { color: var(--faq-heading); font-weight: 600; line-height: 1.3; text-align: left; }
  h1 { font-size: clamp(2rem, 3.7vw, 3rem); letter-spacing: -0.035em; margin-bottom: 24px; }
  h2 { font-size: clamp(1.4rem, 2.3vw, 1.8rem); letter-spacing: -0.02em; margin-bottom: 18px; scroll-margin-top: 88px; }
  p { margin-bottom: 18px; }
  a { color: var(--faq-accent); text-underline-offset: 4px; }
  a:focus-visible, summary:focus-visible { outline: 2px solid var(--faq-accent); outline-offset: 4px; }
  .faq-eyebrow { color: var(--faq-accent); font-size: 0.75rem; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 14px; }
}
.faq-layout { display: grid; grid-template-columns: 200px minmax(0, 1fr); gap: 32px; align-items: start; }
.faq-index { position: sticky; top: 88px; }
.faq-index summary { font-weight: 600; color: var(--faq-heading); cursor: pointer; min-height: 44px; }
.faq-index nav { display: flex; flex-direction: column; border-left: 1px solid var(--faq-divider); padding-left: 16px; }
.faq-index a { display: flex; align-items: center; min-height: 44px; padding: 6px 0; font-size: 0.875rem; text-decoration: none; line-height: 1.5; }
.faq-index a:hover { text-decoration: underline; }
.faq-body { min-width: 0; background: var(--faq-surface); border: 1px solid var(--faq-divider); border-radius: 8px; padding: 44px; }
.faq-section { margin-top: 36px; padding-top: 32px; border-top: 1px solid var(--faq-divider); }
.faq-question { border-bottom: 1px solid var(--faq-divider); scroll-margin-top: 88px; }
.faq-question summary { position: relative; padding: 18px 32px 18px 0; min-height: 56px; color: var(--faq-heading); font-weight: 500; line-height: 1.6; cursor: pointer; list-style: none; }
.faq-question summary::-webkit-details-marker { display: none; }
.faq-question summary::after { content: '+'; position: absolute; right: 4px; top: 18px; color: var(--faq-accent); font-size: 22px; font-weight: 400; line-height: 1.2; }
.faq-question[open] summary { color: var(--faq-accent); }
.faq-question[open] summary::after { content: '−'; }
.faq-answer { padding: 0 24px 4px 0; }
.faq-more { margin-top: 32px; padding: 20px 24px; background: var(--faq-tint); border-left: 3px solid var(--faq-accent); }
.faq-more p { margin-bottom: 4px; }
.faq-more a { display: inline-flex; align-items: center; min-height: 44px; }
@media (max-width: 959px) {
  .faq-page { margin-top: 8px; margin-bottom: 32px; }
  .faq-layout { grid-template-columns: 1fr; gap: 20px; }
  .faq-index { position: static; }
  .faq-index summary { min-height: 44px; padding: 8px 0; }
  .faq-index nav { margin-top: 8px; }
  .faq-body { padding: 28px; }
}
@media (max-width: 599px) {
  .faq-body { padding: 24px 0 0; border: 0; border-top: 1px solid var(--faq-divider); border-radius: 0; background: transparent; }
  .faq-section { margin-top: 28px; padding-top: 24px; }
  .faq-answer { padding-right: 0; }
  .faq-more { padding: 16px 20px; }
}
</style>
